import React from "react";
import styled from "styled-components";
import { Tooltip } from "antd";

import {
  FlexibleContainer,
  RowContainer,
} from "../../../../components/containers/styledComponents";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../../../components/common/styledComponents";
import { NavBar } from "../../../../components/common/NavBar";
import { CompassTag } from "../../../../components/visuals/CompassTag";
import { EngagementStatusHeader } from "../../../../components/common/EngagementStatusHeader";
import { InlineLoadingPlaceholder } from "../../../../components/visuals/InlineLoadingPlaceholder";
import {
  compassLightGreenBackground,
  darkGreen,
  greyText,
} from "../../../../styles";
import downArrowIcon from "../../../../assets/svg/down_arrow_icon.svg";
import { formatReadable } from "../../../../utils/formattingUtils";
import { EngagementRecord } from "../../../../models/stewardshipModels";

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: none;
  background: #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 16px;
  align-self: center;
  transition:
    transform 0.15s ease-in-out,
    background 0.15s ease-in-out;

  &:hover {
    transform: scale(1.08);
    background: #e0e0e0;
  }

  img {
    transform: rotate(90deg);
    width: 20px;
    height: 20px;
  }
`;

const Clamp2 = styled(CompassParagraph)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 820px;
`;

export const AssessmentPageHeader = ({
  engagement,
  engagementId,
  companyName,
  companyLogoUrl,
  metaStrip,
  onBack,
}: AssessmentPageHeaderProps): JSX.Element => (
  <>
    <RowContainer mt={1} mb={1}>
      <NavBar
        address={
          <>
            Engagements /{" "}
            {companyName ? (
              formatReadable(companyName)
            ) : (
              <InlineLoadingPlaceholder label="Loading company" />
            )}{" "}
            / assessment
          </>
        }
      />
    </RowContainer>

    <RowContainer ai="center" jc="flex-start" mt={-16} mb={0}>
      <FlexibleContainer ai="center" jc="flex-start">
        <CompassMainHeader pt={1} ml={12}>
          {engagement?.engagement_name || "Engagement Assessment"}
        </CompassMainHeader>
        {engagementId && (
          <FlexibleContainer ai="center" ml="12px">
            <CompassTag
              tagKey="engagement-id"
              displayValue={engagementId.slice(0, 8)}
              bg={compassLightGreenBackground}
              color={darkGreen}
              size="small"
              hideMargin
            />
          </FlexibleContainer>
        )}
      </FlexibleContainer>

    </RowContainer>

    {engagement?.engagement_obj_desc && (
      <RowContainer ai="flex-start" jc="flex-start" mt={4} mb={4}>
        <Clamp2 ml={20} fontSize={12} color={greyText} lh={1.45}>
          {engagement.engagement_obj_desc}
        </Clamp2>
      </RowContainer>
    )}

    {engagement && (
      <RowContainer
        ai="center"
        jc="flex-start"
        ml={10}
        mt={5}
        mb={0}
        wrap="wrap"
        style={{
          paddingLeft: 10,
          paddingRight: 30,
          boxSizing: "border-box",
          gap: "12px",
          rowGap: "10px",
        }}
      >
        <Tooltip title="Back to engagement" placement="bottom">
          <BackButton
            type="button"
            onClick={onBack}
            aria-label="Back to engagement"
          >
            <img src={downArrowIcon} alt="Back" />
          </BackButton>
        </Tooltip>
        <EngagementStatusHeader
          companyName={companyName || engagement.company}
          companyLogoUrl={companyLogoUrl}
          companyId={engagement.company}
          managerName={engagement.manager_name}
          managerImage={engagement.image || null}
          metaTheme={engagement.meta_theme}
          theme={engagement.theme}
          subTheme={engagement.sub_theme}
        />
      </RowContainer>
    )}

    {engagement && metaStrip && (
      <RowContainer
        ai="center"
        jc="flex-start"
        ml={10}
        mt={10}
        mb={0}
        style={{ paddingLeft: 10, boxSizing: "border-box" }}
      >
        {metaStrip}
      </RowContainer>
    )}
  </>
);

type AssessmentPageHeaderProps = {
  engagement: EngagementRecord | null;
  engagementId: string | undefined;
  companyName: string;
  companyLogoUrl: string;
  metaStrip?: React.ReactNode;
  onBack: () => void;
};
